import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Trash2, Link2, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SourcesLinksTab = ({ onMessage }) => {
  const [sources, setSources] = useState([]);
  const [links, setLinks] = useState([]);
  const [newSource, setNewSource] = useState('');
  const [newLink, setNewLink] = useState({ source_name: '', url: '', note: '' });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [srcRes, linkRes] = await Promise.all([
        axios.get(`${API}/admin/support-sources`, { withCredentials: true }),
        axios.get(`${API}/admin/external-links`, { withCredentials: true })
      ]);
      setSources(srcRes.data);
      setLinks(linkRes.data);
    } catch (e) {
      console.error(e);
    }
  };

  const addSource = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/support-sources`, { source_name: newSource }, { withCredentials: true });
      setNewSource('');
      onMessage('Source added');
      fetchAll();
    } catch (err) { onMessage('Failed'); }
  };

  const deleteSource = async (id) => {
    if (!window.confirm('Delete this source?')) return;
    await axios.delete(`${API}/admin/support-sources/${id}`, { withCredentials: true });
    onMessage('Source deleted');
    fetchAll();
  };

  const addLink = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/external-links`, newLink, { withCredentials: true });
      setNewLink({ source_name: '', url: '', note: '' });
      onMessage('Link added');
      fetchAll();
    } catch (err) { onMessage('Failed'); }
  };

  const deleteLink = async (id) => {
    if (!window.confirm('Delete this link?')) return;
    await axios.delete(`${API}/admin/external-links/${id}`, { withCredentials: true });
    onMessage('Link deleted');
    fetchAll();
  };

  return (
    <div data-testid="admin-sources-links-tab" className="space-y-6">
      {/* Support Sources */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
        <h2 className="text-2xl font-bold text-slate-900 mb-2 flex items-center gap-2">
          <Link2 className="w-6 h-6 text-blue-600" /> Support Sources
        </h2>
        <p className="text-sm text-slate-600 mb-6">
          These appear as options when adding external support entries.
        </p>
        <form data-testid="add-source-form" onSubmit={addSource} className="flex gap-2 mb-6">
          <input
            data-testid="source-name-input"
            type="text"
            placeholder="New source (e.g., Wycliffe Germany)"
            value={newSource}
            onChange={(e) => setNewSource(e.target.value)}
            required
            className="flex-1 px-4 py-3 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500"
          />
          <button data-testid="add-source-btn" type="submit"
                  className="px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700">
            <Plus className="w-5 h-5" />
          </button>
        </form>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {sources.map(s => (
            <div key={s.id} className="flex justify-between items-center p-3 border border-slate-200 rounded-xl">
              <span className="text-slate-800">{s.source_name}</span>
              <button data-testid={`delete-source-btn-${s.id}`} onClick={() => deleteSource(s.id)}
                      className="text-red-600 hover:text-red-700">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* External Support Links */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
        <h2 className="text-2xl font-bold text-slate-900 mb-2 flex items-center gap-2">
          <ExternalLink className="w-6 h-6 text-blue-600" /> Public Wycliffe Support Links
        </h2>
        <p className="text-sm text-slate-600 mb-6">
          These appear in the "Support Through Wycliffe" section on the public mission page.
        </p>
        <form data-testid="add-link-form" onSubmit={addLink} className="space-y-3 mb-6">
          <input
            data-testid="link-name-input"
            type="text"
            placeholder="Display name (e.g., Wycliffe Norway)"
            value={newLink.source_name}
            onChange={(e) => setNewLink({ ...newLink, source_name: e.target.value })}
            required
            className="w-full px-4 py-3 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500"
          />
          <input
            data-testid="link-url-input"
            type="url"
            placeholder="https://give.wycliffe.no/..."
            value={newLink.url}
            onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
            required
            className="w-full px-4 py-3 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500"
          />
          <textarea
            data-testid="link-note-input"
            placeholder="Optional note (e.g., giving instructions, reference number)"
            value={newLink.note}
            onChange={(e) => setNewLink({ ...newLink, note: e.target.value })}
            rows="2"
            className="w-full px-4 py-3 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500"
          />
          <button data-testid="add-link-btn" type="submit"
                  className="w-full px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 flex items-center justify-center gap-2">
            <Plus className="w-5 h-5" /> Add Public Link
          </button>
        </form>
        <div className="space-y-2">
          {links.length === 0 ? (
            <p className="text-slate-500 text-sm">No public links yet.</p>
          ) : links.map(l => (
            <div key={l.id} className="flex justify-between items-start p-3 border border-slate-200 rounded-xl gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-slate-800 font-medium">{l.source_name}</p>
                <p className="text-xs text-slate-500 truncate">{l.url}</p>
                {l.note && <p className="text-xs text-slate-600 mt-1 whitespace-pre-wrap">{l.note}</p>}
              </div>
              <button data-testid={`delete-link-btn-${l.id}`} onClick={() => deleteLink(l.id)}
                      className="text-red-600 hover:text-red-700 flex-shrink-0">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
